<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  resolution: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();

const summaryItems = computed(() => {
  const summary = props.resolution.summary;
  return [
    {
      key: 'total_lines',
      label: t('BROADCASTS.COMPOSER.RESOLUTION.TOTAL_LINES'),
      value: summary.total_lines,
      always: true,
    },
    {
      key: 'blank_lines',
      label: t('BROADCASTS.COMPOSER.RESOLUTION.BLANK_LINES'),
      value: summary.blank_lines,
    },
    {
      key: 'ready',
      label: t('BROADCASTS.COMPOSER.RESOLUTION.READY'),
      value: summary.ready,
      always: true,
    },
    {
      key: 'not_found',
      label: t('BROADCASTS.COMPOSER.RESOLUTION.NOT_FOUND'),
      value: summary.not_found,
    },
    {
      key: 'ambiguous',
      label: t('BROADCASTS.COMPOSER.RESOLUTION.AMBIGUOUS'),
      value: summary.ambiguous,
    },
    {
      key: 'missing_telegram_id',
      label: t('BROADCASTS.COMPOSER.RESOLUTION.MISSING_TELEGRAM_ID'),
      value: summary.missing_telegram_id,
    },
    {
      key: 'contact_inbox_not_found',
      label: t('BROADCASTS.COMPOSER.RESOLUTION.CONTACT_INBOX_NOT_FOUND'),
      value: summary.contact_inbox_not_found,
    },
    {
      key: 'conversation_not_found',
      label: t('BROADCASTS.COMPOSER.RESOLUTION.CONVERSATION_NOT_FOUND'),
      value: summary.conversation_not_found,
    },
    {
      key: 'excluded_by_team',
      label: t('BROADCASTS.COMPOSER.RESOLUTION.EXCLUDED_BY_TEAM'),
      value: summary.excluded_by_team,
    },
    {
      key: 'excluded_by_courier_app',
      label: t('BROADCASTS.COMPOSER.RESOLUTION.EXCLUDED_BY_COURIER_APP'),
      value: summary.excluded_by_courier_app,
    },
    {
      key: 'invalid',
      label: t('BROADCASTS.COMPOSER.RESOLUTION.INVALID'),
      value: summary.invalid,
    },
    {
      key: 'duplicates',
      label: t('BROADCASTS.COMPOSER.RESOLUTION.DUPLICATE'),
      value: summary.duplicates,
    },
    {
      key: 'unique_targets',
      label: t('BROADCASTS.COMPOSER.RESOLUTION.UNIQUE_TARGETS'),
      value: summary.unique_targets,
      always: true,
    },
  ].filter(item => item.always || item.value > 0);
});

const statusLabel = status => {
  switch (status) {
    case 'ready':
      return t('BROADCASTS.COMPOSER.RESOLUTION.READY');
    case 'not_found':
      return t('BROADCASTS.COMPOSER.RESOLUTION.NOT_FOUND');
    case 'ambiguous':
      return t('BROADCASTS.COMPOSER.RESOLUTION.AMBIGUOUS');
    case 'missing_telegram_id':
      return t('BROADCASTS.COMPOSER.RESOLUTION.MISSING_TELEGRAM_ID');
    case 'contact_inbox_not_found':
      return t('BROADCASTS.COMPOSER.RESOLUTION.CONTACT_INBOX_NOT_FOUND');
    case 'conversation_not_found':
      return t('BROADCASTS.COMPOSER.RESOLUTION.CONVERSATION_NOT_FOUND');
    case 'excluded_by_team':
      return t('BROADCASTS.COMPOSER.RESOLUTION.EXCLUDED_BY_TEAM');
    case 'excluded_by_courier_app':
      return t('BROADCASTS.COMPOSER.RESOLUTION.EXCLUDED_BY_COURIER_APP');
    case 'duplicate':
      return t('BROADCASTS.COMPOSER.RESOLUTION.DUPLICATE');
    default:
      return t('BROADCASTS.COMPOSER.RESOLUTION.INVALID');
  }
};

const statusClass = status => {
  if (status === 'ready') return 'bg-n-teal-3 text-n-teal-11';
  if (status === 'duplicate') return 'bg-n-amber-3 text-n-amber-11';
  return 'bg-n-ruby-3 text-n-ruby-11';
};

const statusDescription = entry => {
  switch (entry.status) {
    case 'ready':
      return t('BROADCASTS.COMPOSER.RESOLUTION.READY_DESCRIPTION', {
        conversationId: entry.target.conversation_id,
      });
    case 'not_found':
      return t('BROADCASTS.COMPOSER.RESOLUTION.NOT_FOUND_DESCRIPTION');
    case 'ambiguous':
      return t('BROADCASTS.COMPOSER.RESOLUTION.AMBIGUOUS_DESCRIPTION', {
        count: entry.match_count,
      });
    case 'missing_telegram_id':
      return t(
        'BROADCASTS.COMPOSER.RESOLUTION.MISSING_TELEGRAM_ID_DESCRIPTION'
      );
    case 'contact_inbox_not_found':
      return t(
        'BROADCASTS.COMPOSER.RESOLUTION.CONTACT_INBOX_NOT_FOUND_DESCRIPTION'
      );
    case 'conversation_not_found':
      return t(
        'BROADCASTS.COMPOSER.RESOLUTION.CONVERSATION_NOT_FOUND_DESCRIPTION'
      );
    case 'excluded_by_team':
      return t('BROADCASTS.COMPOSER.RESOLUTION.EXCLUDED_BY_TEAM_DESCRIPTION');
    case 'excluded_by_courier_app':
      return t(
        'BROADCASTS.COMPOSER.RESOLUTION.EXCLUDED_BY_COURIER_APP_DESCRIPTION'
      );
    case 'duplicate':
      return t('BROADCASTS.COMPOSER.RESOLUTION.DUPLICATE_DESCRIPTION', {
        conversationId: entry.target.conversation_id,
      });
    default:
      return t('BROADCASTS.COMPOSER.RESOLUTION.INVALID_DESCRIPTION');
  }
};
</script>

<template>
  <div class="flex flex-col gap-3 rounded-lg bg-n-alpha-1 p-3">
    <div class="flex flex-wrap gap-2">
      <div
        v-for="item in summaryItems"
        :key="item.key"
        class="rounded-md border border-n-weak bg-n-solid-2 px-2 py-1 text-xs text-n-slate-11"
      >
        {{ item.label }}:
        <span class="font-medium text-n-slate-12">{{ item.value }}</span>
      </div>
    </div>

    <div class="max-h-80 overflow-y-auto rounded-lg border border-n-weak">
      <div
        v-for="entry in resolution.entries"
        :key="entry.line"
        class="flex flex-col gap-2 border-b border-n-weak bg-n-solid-2 p-3 last:border-b-0 sm:flex-row sm:items-start"
      >
        <div class="min-w-0 flex-1">
          <div class="flex items-center gap-2">
            <span class="text-xs text-n-slate-10">
              {{
                t('BROADCASTS.COMPOSER.RESOLUTION.LINE', { line: entry.line })
              }}
            </span>
            <span class="truncate text-sm font-medium text-n-slate-12">
              {{ entry.input }}
            </span>
          </div>
          <div class="mt-1 text-xs text-n-slate-10">
            {{
              t('BROADCASTS.COMPOSER.RESOLUTION.NORMALIZED', {
                value: entry.normalized_input,
              })
            }}
          </div>
          <div class="mt-1 text-xs text-n-slate-11">
            {{ statusDescription(entry) }}
          </div>
        </div>
        <span
          class="w-fit shrink-0 rounded-md px-2 py-1 text-xs font-medium"
          :class="statusClass(entry.status)"
        >
          {{ statusLabel(entry.status) }}
        </span>
      </div>
    </div>
  </div>
</template>
